import React, { useState } from 'react';

const Footer = () => {
  const [isLegalInfoExpanded, setIsLegalInfoExpanded] = useState(false);

  const toggleLegalInfo = () => {
    setIsLegalInfoExpanded(!isLegalInfoExpanded);
  };

  return (
    <footer className="bg-slate-900 border-t border-slate-700">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="text-center">
          <p className="text-gray-400 text-sm mb-4">
            © 2025 D.C. Technology Corp. All rights reserved.
          </p>
          
          {/* Essential Legal and Regulatory Information Button */}
          <button
            onClick={toggleLegalInfo}
            className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors duration-200 text-sm font-medium"
            aria-expanded={isLegalInfoExpanded}
          >
            <span>Essential Legal and Regulatory Information</span>
            <svg 
              className={`w-4 h-4 ml-2 transition-transform duration-200 ${isLegalInfoExpanded ? 'rotate-180' : ''}`}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Expandable Legal Information Section */}
      <div 
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isLegalInfoExpanded ? 'max-h-screen' : 'max-h-0'
        }`}
      >
        <div className="bg-slate-800 border-t border-slate-700">
          <div className="container mx-auto px-4 py-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Canadian/Ontario Electrical Standards */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                  <span className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center mr-3">
                    🍁
                  </span>
                  Canadian & Ontario Electrical Standards
                </h3>
                <div className="space-y-4 text-gray-300 text-sm">
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Ontario Electrical Safety Code (OESC) - 2025</h4>
                    <p className="mb-2">
                      <strong>Effective Date:</strong> May 1, 2025 - The 2024 Canadian Electrical Code, Part I (CEC-2024) 
                      has been adopted by Ontario through Ontario Regulation 164/99.
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Enhanced energy storage systems guidelines for lithium-ion batteries</li>
                      <li>Updated solar panel installation requirements</li>
                      <li>Reinforced "Single Point Grounding" concepts</li>
                      <li>New load management system clarifications</li>
                    </ul>
                  </div>
                  
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">CSA Z462 - Workplace Electrical Safety</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Arc flash and electrical shock hazard protection</li>
                      <li>Qualified vs. unqualified personnel definitions</li>
                      <li>Electrical safety procedures for workplace environments</li>
                    </ul>
                  </div>

                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Enforcement & Compliance</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Enforced by the Electrical Safety Authority (ESA)</li>
                      <li>All electrical equipment must bear certification marks</li>
                      <li>Mandatory compliance for all electrical installations in Ontario</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Workplace Safety Standards */}
              <div>
                <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
                  <span className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center mr-3">
                    ⚠️
                  </span>
                  Workplace Safety Standards
                </h3>
                <div className="space-y-4 text-gray-300 text-sm">
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Canada Occupational Health & Safety Regulations</h4>
                    <p className="mb-2">
                      <strong>Current to September 2025:</strong> SOR/86-304 requires electrical equipment design, 
                      construction, and installation to meet Canadian Electrical Code standards.
                    </p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Proper training requirements for electrical workers</li>
                      <li>Use of certified equipment and safety procedures</li>
                      <li>Regular workplace inspections and compliance verification</li>
                    </ul>
                  </div>
                  
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Employer Responsibilities</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Ensure electrical workers are properly trained and qualified</li>
                      <li>Use only certified electrical equipment</li>
                      <li>Follow best practices for temporary installations</li>
                      <li>Maintain compliance with safety inspections</li>
                    </ul>
                  </div>

                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Key Safety Areas</h4>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Arc flash and blast protection protocols</li>
                      <li>Electrical shock prevention measures</li>
                      <li>Lockout/tagout procedures</li>
                      <li>Personal protective equipment requirements</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Disclaimers */}
            <div className="mt-8 bg-amber-900/20 border border-amber-600/30 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-amber-400 mb-3 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Important Disclaimers
              </h3>
              <div className="text-amber-200 text-sm space-y-2">
                <p>
                  <strong>Current Information:</strong> Electrical codes and safety standards are updated regularly. 
                  Always verify the most current versions of applicable codes and standards before beginning any electrical work.
                </p>
                <p>
                  <strong>Professional Consultation:</strong> This information is provided for general awareness only. 
                  Always consult with licensed electrical professionals and official regulatory bodies for specific project requirements.
                </p>
                <p>
                  <strong>Official Sources:</strong> For the most up-to-date information, refer to:
                </p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Electrical Safety Authority (ESA) - <span className="text-blue-300">esasafe.com</span></li>
                  <li>Canadian Standards Association (CSA) - <span className="text-blue-300">csa.ca</span></li>
                  <li>Ontario Government Regulations - <span className="text-blue-300">ontario.ca</span></li>
                  <li>Government of Canada Occupational Health & Safety - <span className="text-blue-300">laws.justice.gc.ca</span></li>
                </ul>
              </div>
            </div>

            {/* Last Updated Information */}
            <div className="mt-6 text-center text-xs text-gray-500">
              <p>
                Last updated: January 2025 | Information compiled from current Canadian and Ontario electrical safety regulations
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;